"use client";

import Link from "next/link";
import { ArrowRight, FileText, ScrollText, Shield } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";

export interface LongFormMeta {
  eyebrow: string;
  title: string;
  titleAccent?: string;
  description?: string;
  effectiveDate?: string;
  lastUpdated?: string;
  version?: string;
  ctaPrimary?: { label: string; href: string };
  ctaSecondary?: { label: string; href: string };
}

export interface LongFormSection {
  id: string;
  number: string;
  title: string;
  body: string;
  subsections?: { id: string; title: string; body: string }[];
  callout?: { title: string; body: string };
  bullets?: string[];
}

export interface LongFormPageProps {
  meta: LongFormMeta;
  sections: LongFormSection[];
  toc?: { id: string; label: string }[];
  trustBar?: string[];
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet";
  heroIcon?: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
}

export function LongFormPage({ meta, sections, toc, trustBar, accent, heroIcon: HeroIcon }: LongFormPageProps) {
  const accentVar = `var(--color-${accent})`;

  return (
    <>
      <Section id="top" className="relative overflow-hidden pt-12 pb-12 sm:pt-16 sm:pb-16">
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div
            className="absolute top-1/4 right-1/4 h-72 w-72 rounded-full opacity-25 blur-[140px]"
            style={{ background: accentVar }}
          />
        </div>
        <Container>
          <Reveal>
            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass mb-6">
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: accentVar }} />
                <span className="eyebrow-tiny" style={{ color: accentVar }}>{meta.eyebrow}</span>
              </div>
            </div>
          </Reveal>

          <Reveal delay={80}>
            <h1 className="display-hero text-balance">
              {meta.title}
              {meta.titleAccent && (
                <>
                  {" "}
                  <span
                    className="bg-clip-text text-transparent"
                    style={{ backgroundImage: `linear-gradient(135deg, var(--color-crimson), ${accentVar}, var(--color-cyan))` }}
                  >
                    {meta.titleAccent}
                  </span>
                </>
              )}
            </h1>
          </Reveal>

          {meta.description && (
            <Reveal delay={140}>
              <p className="mt-5 max-w-3xl text-body leading-relaxed text-[rgb(var(--fg-muted))]">{meta.description}</p>
            </Reveal>
          )}

          {(meta.effectiveDate || meta.lastUpdated || meta.version) && (
            <Reveal delay={180}>
              <div className="mt-6 flex flex-wrap items-center gap-3 text-xs text-[rgb(var(--fg-subtle))]">
                {meta.effectiveDate && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full glass">
                    <span className="eyebrow-tiny">Effective</span>
                    <span className="font-semibold">{meta.effectiveDate}</span>
                  </span>
                )}
                {meta.lastUpdated && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full glass">
                    <span className="eyebrow-tiny">Updated</span>
                    <span className="font-semibold">{meta.lastUpdated}</span>
                  </span>
                )}
                {meta.version && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full glass">
                    <span className="eyebrow-tiny">Version</span>
                    <span className="font-semibold">{meta.version}</span>
                  </span>
                )}
              </div>
            </Reveal>
          )}

          {(meta.ctaPrimary || meta.ctaSecondary) && (
            <Reveal delay={220}>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                {meta.ctaPrimary && (
                  <Link
                    href={meta.ctaPrimary.href}
                    className="btn-primary-gradient h-12 px-7 inline-flex items-center gap-2 rounded-full font-semibold text-base shadow-2xl"
                    style={{ boxShadow: `0 25px 50px -12px ${accentVar}40` }}
                  >
                    {meta.ctaPrimary.label} <ArrowRight className="h-4 w-4" />
                  </Link>
                )}
                {meta.ctaSecondary && (
                  <Link
                    href={meta.ctaSecondary.href}
                    className="btn-glass h-12 px-7 inline-flex items-center gap-2 rounded-full font-semibold text-base"
                  >
                    {meta.ctaSecondary.label}
                  </Link>
                )}
              </div>
            </Reveal>
          )}
        </Container>
      </Section>

      {toc && toc.length > 0 && (
        <Section className="!py-12" id="toc">
          <Container>
            <Reveal>
              <div className="glass rounded-2xl p-6">
                <div className="eyebrow-tiny mb-3" style={{ color: accentVar }}>Table of Contents</div>
                <ol className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                  {toc.map((item) => (
                    <li key={item.id} className="flex items-baseline gap-2">
                      <span className="text-xs font-mono text-[rgb(var(--fg-subtle))]">{item.id}.</span>
                      <a href={`#${item.id}`} className="hover:underline" style={{ color: accentVar }}>
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ol>
              </div>
            </Reveal>
          </Container>
        </Section>
      )}

      {sections.map((section, i) => (
        <Section key={section.id} id={section.id} className="relative">
          <Container>
            <Reveal>
              <div className="grid lg:grid-cols-[1fr_3fr] gap-8">
                <div className="lg:sticky lg:top-24 lg:self-start">
                  <div
                    className="eyebrow-tiny font-bold"
                    style={{ color: accentVar }}
                  >
                    Section {section.number}
                  </div>
                  <h2 className="mt-1 text-2xl">{section.title}</h2>
                </div>
                <div className="space-y-4 max-w-3xl">
                  {section.body.split("\n\n").map((para, j) => (
                    <p key={j} className="text-body-sm text-[rgb(var(--fg-muted))] leading-relaxed">
                      {para}
                    </p>
                  ))}
                  {section.subsections?.map((sub) => (
                    <div key={sub.id} id={sub.id} className="mt-6">
                      <h3 className="text-lg">{sub.title}</h3>
                      <div className="mt-2 space-y-3">
                        {sub.body.split("\n\n").map((para, j) => (
                          <p key={j} className="text-body-sm text-[rgb(var(--fg-muted))] leading-relaxed">
                            {para}
                          </p>
                        ))}
                      </div>
                    </div>
                  ))}
                  {section.bullets && section.bullets.length > 0 && (
                    <ul className="mt-4 space-y-2">
                      {section.bullets.map((b, j) => (
                        <li key={j} className="flex items-start gap-2 text-sm text-[rgb(var(--fg-muted))] leading-relaxed">
                          <span
                            className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0"
                            style={{ background: accentVar }}
                          />
                          {b}
                        </li>
                      ))}
                    </ul>
                  )}
                  {section.callout && (
                    <div
                      className="mt-4 rounded-xl p-4"
                      style={{
                        background: `color-mix(in oklch, ${accentVar} 6%, transparent)`,
                        boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accentVar} 18%, transparent)`,
                      }}
                    >
                      <div className="eyebrow-tiny mb-1" style={{ color: accentVar }}>
                        {section.callout.title}
                      </div>
                      <p className="text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{section.callout.body}</p>
                    </div>
                  )}
                </div>
              </div>
            </Reveal>
          </Container>
        </Section>
      ))}

      {trustBar && trustBar.length > 0 && (
        <Section className="!py-12" id="contact">
          <Container>
            <Reveal>
              <div className="glass rounded-2xl p-6">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                  {trustBar.map((item, i) => {
                    const accents = [
                      "var(--color-crimson)",
                      "var(--color-amber)",
                      "var(--color-purple)",
                      "var(--color-cyan)",
                      "var(--color-emerald)",
                    ] as const;
                    const a = accents[i % accents.length]!;
                    return (
                      <div key={item} className="flex items-center gap-2">
                        <Shield className="h-4 w-4 shrink-0" style={{ color: a }} />
                        <span className="text-xs font-semibold leading-tight">{item}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Reveal>
          </Container>
        </Section>
      )}
    </>
  );
}